import { useState } from "react";
import { Modal } from "react-bootstrap";
import { errorToast, successToast } from "../../Toasts/Toasts";
import { cancelAggregatorTransaction, approveAggregatorTransaction, rejectAggregatorTransaction } from "../../../services/supplierService";
import localStorageStore from "../../../utils/localStorageStore";
import { userRoleConstants } from "../../../utils/constants";
import moment from "moment";
import { formatCreditBalance } from "../../../utils/sharedFunctions";

const BillingLogModal = ({ showBillingLog, setShowBillingLog, transactionData = {}, onSuccess }) => {
  const isAdmin = parseInt(localStorageStore.getRole()) === userRoleConstants.ADMIN_ID;
  const isAccountant = parseInt(localStorageStore.getRole()) === userRoleConstants.ACCOUNTANT_ID;
  const [remarks, setRemarks] = useState(transactionData?.admin_remarks || "");
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setShowBillingLog(false);
    setRemarks("");
    setLoading(false);
  };
  const isPending = transactionData?.status?.toLowerCase() === "pending" && !transactionData?.approved_by_id && !transactionData?.cancelled_by_id && !transactionData?.rejected_by_id;
  const isCompleted = !!transactionData?.approved_by_id;
  const isCancelled = !!transactionData?.cancelled_by_id;
  const isRejected = !!transactionData?.rejected_by_id;
  const handleApprove = async () => {
    setLoading(true);
    try {
      await approveAggregatorTransaction(transactionData.id, remarks);
      successToast("Transaction approved successfully");
      setRemarks("");
      setShowBillingLog(false);
      onSuccess?.();
    } catch (error) {
      errorToast("Failed to approve transaction");
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    setLoading(true);
    try {
      await rejectAggregatorTransaction(transactionData.id, remarks);
      successToast("Transaction rejected successfully");
      setRemarks("");
      setShowBillingLog(false);
      onSuccess?.();
    } catch (error) {
      errorToast("Failed to reject transaction");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelTransaction = async () => {
    setLoading(true);
    try {
      await cancelAggregatorTransaction(transactionData.id, remarks);
      setShowBillingLog(false);
      successToast("Transaction cancelled");
      onSuccess?.();
    } catch (error) {
      errorToast("Failed to cancel transaction");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={showBillingLog} size="md" centered>
      <div className="modal-simple modal-edit-user">
        <div className="modal-content p-3 p-md-3">
          <Modal.Body className="p-2">
            <button type="button" className="btn-close" onClick={handleClose} disabled={loading} aria-label="Close" />

            <div className="text-left mb-4">
              <h3 className="mb-2">Transaction Details</h3>
            </div>
            <div className="row mb-3 border-bottom pb-3">
              <div className="col-lg-12">
                <div className="row g-2">
                  <div className="col-lg-3">
                    <label htmlFor="transaction-id" className="form-label">ID</label>
                    <p className="mb-0 transaction-value">{transactionData?.id || "N/A"}</p>
                  </div>
                  <div className="col-lg-3">
                    <label htmlFor="transaction-date" className="form-label">Date</label>
                    <p className="mb-0 transaction-value">{transactionData?.date || "N/A"}</p>
                  </div>
                  <div className="col-lg-3">
                    <label htmlFor="transaction-type" className="form-label">Type</label>
                    <p className="mb-0 transaction-value">{transactionData?.type || "N/A"}</p>
                  </div>
                  <div className="col-lg-3">
                    <label htmlFor="transaction-currency" className="form-label">Currency</label>
                    <p className="mb-0 transaction-value">{transactionData?.local_currency || "N/A"}</p>
                  </div>
                  <div className="col-lg-3">
                    <label htmlFor="transaction-amount" className="form-label">Amount</label>
                    <p className="mb-0 transaction-value">{transactionData?.amount_usd ? `${formatCreditBalance(transactionData?.amount_usd, 2)} USD` : "N/A"}</p>
                  </div>
                  <div className="col-lg-3">
                    <label htmlFor="transaction-exchange-rate" className="form-label">Exchange Rate</label>
                    <p className="mb-0 transaction-value">{transactionData?.exchange_rate ? formatCreditBalance(transactionData?.exchange_rate, 4) : "N/A"}</p>
                  </div>
                  <div className="col-lg-3">
                    <label htmlFor="transaction-local-amount" className="form-label">Local Amount</label>
                    <p className="mb-0 transaction-value">{transactionData?.amount_local ? `${formatCreditBalance(transactionData?.amount_local, 2)} ${transactionData?.local_currency || ""}` : "N/A"}</p>
                  </div>
                  <div className="col-lg-3">
                    <label htmlFor="transaction-commission" className="form-label">Commission</label>
                    <p className="mb-0 transaction-value">{transactionData?.commission || "N/A"}</p>
                  </div>
                  <div className="col-lg-12 mt-3">
                    <label htmlFor="transaction-remarks" className="form-label">Remarks</label>
                    <p className="mb-0 transaction-value">{transactionData?.remarks || "No remarks available"}</p>
                  </div>
                </div>
                <div className="row mt-3">
                  <h5 className="mb-2">
                    Supplier Account <span className="text-muted">(for this transaction)</span>
                  </h5>
                  <div className="bg-lighter px-3 py-2 rounded me-auto d-flex">
                    <div className="me-5">
                      <p className="mb-0 transaction-value">{transactionData?.BankAccount?.bank_name || transactionData?.bank_name || "N/A"}</p>
                      <span className="text-body">Bank Name</span>
                    </div>
                    <div className="me-0">
                      <p className="mb-0 transaction-value">{transactionData?.bankAccount || transactionData?.BankAccount?.account_number || "N/A"}</p>
                      <span className="text-body">Account No.</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-left mb-4">
              <h5 className="card-title m-0 me-2 pt-1 mb-2 d-flex align-items-center">
                <i className="ti ti-list-details ms-n1 me-2"></i>
                Log
              </h5>
            </div>

            <div className="pt-2">
              <ul className="timeline ms-1 mb-0">
                <li className="timeline-item timeline-item-transparent ps-4">
                  <span className="timeline-point timeline-point-secondary"></span>
                  <div className="timeline-event pt-3">
                    <div className="timeline-header">
                      <h6 className="mb-0">
                        Transaction created by <span className="text-warning">{transactionData?.created_by?.name || "N/A"}</span>
                      </h6>
                    </div>
                    <p className="mb-0 small text-muted">{transactionData?.createdAt ? moment.utc(transactionData?.createdAt).format("DD MMM YY, hh:mm A") : "N/A"}</p>
                  </div>
                </li>
                {isCompleted && (
                  <li className="timeline-item timeline-item-transparent ps-4">
                    <span className="timeline-point timeline-point-success"></span>
                    <div className="timeline-event pt-3 pb-0">
                      <div className="timeline-header">
                        <h6 className="mb-0">
                          Approved by <span className="text-success">{transactionData?.approved_by?.name || "N/A"}</span>
                        </h6>
                      </div>
                      <p className="mb-0 small text-muted">{transactionData?.approved_at ? moment.utc(transactionData?.approved_at).format("DD MMM YY, hh:mm A") : "N/A"}</p>
                    </div>
                  </li>
                )}
                {isCancelled && (
                  <li className="timeline-item timeline-item-transparent ps-4">
                    <span className="timeline-point timeline-point-danger"></span>
                    <div className="timeline-event pt-3 pb-0">
                      <div className="timeline-header">
                        <h6 className="mb-0">
                          Cancelled by <span className="text-danger">{transactionData?.cancelled_by?.name || "N/A"}</span>
                        </h6>
                      </div>
                      <p className="mb-0 small text-muted">{transactionData?.cancelled_at ? moment.utc(transactionData?.cancelled_at).format("DD MMM YY, hh:mm A") : "N/A"}</p>
                    </div>
                  </li>
                )}
                {isRejected && (
                  <li className="timeline-item timeline-item-transparent ps-4">
                    <span className="timeline-point timeline-point-warning"></span>
                    <div className="timeline-event pt-3 pb-0">
                      <div className="timeline-header">
                        <h6 className="mb-0">
                          Rejected by <span className="text-warning">{transactionData?.rejected_by?.name || "N/A"}</span>
                        </h6>
                      </div>
                      <p className="mb-0 small text-muted">{transactionData?.rejected_at ? moment.utc(transactionData?.rejected_at).format("DD MMM YY, hh:mm A") : "N/A"}</p>
                    </div>
                  </li>
                )}
              </ul>
            </div>
            <div className="mb-3 mt-3">
              <label className="form-label">Remarks</label>
              {transactionData?.admin_remarks ? (
                <p className="mb-0">{transactionData.admin_remarks}</p>
              ) : isPending && isAdmin ? (
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Enter remarks (optional)..."
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  disabled={loading}
                />
              ) : (
                <p className="mb-0 text-muted">No remarks available</p>
              )}
            </div>
            <div className="d-flex flex-wrap justify-content-end">
              {isPending && isAdmin ? (
                <>
                  <button type="button" className="btn btn-sm btn-danger rounded-pill me-2" onClick={handleReject} disabled={loading}>
                    Reject
                  </button>
                  <button type="button" className="btn btn-sm btn-success rounded-pill" onClick={handleApprove} disabled={loading}>
                    Approve
                  </button>
                </>
              ) : null}
              {(isPending && isAccountant) || (isCompleted && !isCancelled && !isAccountant) ? (
                <button type="button" className="btn btn-sm btn-warning rounded-pill me-2" onClick={handleCancelTransaction} disabled={loading}>
                  Cancel
                </button>
              ) : null}
            </div>
          </Modal.Body>
        </div>
      </div>
    </Modal>
  );
};

export default BillingLogModal;
